import React, { useMemo, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useStudyStore } from '../../store/useStudyStore'
import { GameResult } from '../../types/database'
import { Trophy, Medal, Award, Clock, Target, Zap, Filter, ChevronDown, Star } from 'lucide-react'

interface GameLeaderboardProps {
  gameId?: string
  classId?: string
  limit?: number
  showFilters?: boolean
}

type SortBy = 'time' | 'score' | 'recent'
type FilterBy = 'all' | 'won' | 'perfect'

/**
 * Component bảng xếp hạng game theo thời gian và điểm số
 */
export const GameLeaderboard: React.FC<GameLeaderboardProps> = ({ 
  gameId, 
  classId,
  limit = 10,
  showFilters = true 
}) => {
  const { gameResults, currentUser } = useStudyStore()
  const [sortBy, setSortBy] = useState<SortBy>('time')
  const [filterBy, setFilterBy] = useState<FilterBy>('won')
  const [showDetails, setShowDetails] = useState<string | null>(null)

  // Filter và sort results
  const rankedResults = useMemo(() => {
    let filtered = [...gameResults]

    // Filter by gameId nếu có
    if (gameId) {
      filtered = filtered.filter(r => r.gameId === gameId)
    }

    // Filter by classId nếu có
    if (classId) {
      filtered = filtered.filter(r => r.classId === classId)
    }

    // Filter by status
    if (filterBy === 'won') {
      filtered = filtered.filter(r => r.won)
    } else if (filterBy === 'perfect') {
      filtered = filtered.filter(r => r.percentage === 100)
    }

    // Sort
    if (sortBy === 'time') {
      // Sắp xếp theo thời gian (nhanh nhất lên đầu)
      filtered.sort((a, b) => {
        // Chỉ xét những người thắng với điểm tương đương
        if (a.won && b.won && a.percentage === b.percentage) {
          return a.timeSpent - b.timeSpent
        }
        // Người có điểm cao hơn xếp trước
        return b.percentage - a.percentage
      })
    } else if (sortBy === 'score') {
      // Sắp xếp theo điểm cao nhất
      filtered.sort((a, b) => {
        if (a.percentage === b.percentage) {
          return a.timeSpent - b.timeSpent // Cùng điểm thì nhanh hơn xếp trước
        }
        return b.percentage - a.percentage
      })
    } else if (sortBy === 'recent') {
      // Sắp xếp theo thời gian gần nhất
      filtered.sort((a, b) => 
        new Date(b.completedAt).getTime() - new Date(a.completedAt).getTime()
      )
    }

    return filtered.slice(0, limit)
  }, [gameResults, gameId, classId, sortBy, filterBy, limit])

  if (gameResults.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-8 text-center">
        <div className="text-6xl mb-4">🏆</div>
        <h3 className="text-xl font-bold text-gray-700 mb-2">Chưa có kết quả</h3>
        <p className="text-gray-500">Hãy chơi game để xuất hiện trong bảng xếp hạng!</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 rounded-xl shadow-lg p-4 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Trophy className="w-8 h-8" />
            <div>
              <h2 className="text-2xl font-bold">🏆 Bảng Xếp Hạng</h2>
              <p className="text-sm text-white/90">Top {rankedResults.length} kết quả xuất sắc</p>
            </div>
          </div>
          
          {showFilters && (
            <div className="flex items-center gap-2">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as SortBy)}
                className="px-3 py-1.5 rounded-lg text-sm font-medium bg-white/20 backdrop-blur-sm text-white border border-white/30 cursor-pointer hover:bg-white/30 transition-colors"
              >
                <option value="time" className="text-gray-900">⚡ Nhanh nhất</option>
                <option value="score" className="text-gray-900">🎯 Điểm cao</option>
                <option value="recent" className="text-gray-900">🕐 Gần nhất</option>
              </select>
              
              <select
                value={filterBy}
                onChange={(e) => setFilterBy(e.target.value as FilterBy)}
                className="px-3 py-1.5 rounded-lg text-sm font-medium bg-white/20 backdrop-blur-sm text-white border border-white/30 cursor-pointer hover:bg-white/30 transition-colors"
              >
                <option value="all" className="text-gray-900">Tất cả</option>
                <option value="won" className="text-gray-900">✅ Thắng</option>
                <option value="perfect" className="text-gray-900">💯 Hoàn hảo</option>
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Leaderboard */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b-2 border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                  Hạng
                </th>
                <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                  Người chơi
                </th>
                <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider">
                  Điểm
                </th>
                <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider">
                  Thời gian
                </th>
                <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider">
                  Phòng
                </th>
                <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider">
                  Đánh giá
                </th>
                <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider">
                  Ngày
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {rankedResults.map((result, index) => (
                <LeaderboardRow
                  key={result.id}
                  result={result}
                  rank={index + 1}
                  isCurrentUser={result.userId === currentUser?.id}
                  showDetails={showDetails === result.id}
                  onToggleDetails={() => setShowDetails(showDetails === result.id ? null : result.id)}
                  sortBy={sortBy}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Personal Best */}
      {currentUser && (
        <PersonalBest 
          gameId={gameId}
          userId={currentUser.id}
        />
      )}
    </div>
  )
}

/**
 * Leaderboard Row Component
 */
const LeaderboardRow: React.FC<{
  result: GameResult
  rank: number
  isCurrentUser: boolean
  showDetails: boolean
  onToggleDetails: () => void
  sortBy: SortBy
}> = ({ result, rank, isCurrentUser, showDetails, onToggleDetails, sortBy }) => {
  const getRankIcon = (rank: number) => {
    if (rank === 1) return <Trophy className="w-6 h-6 text-yellow-500" />
    if (rank === 2) return <Medal className="w-6 h-6 text-gray-400" />
    if (rank === 3) return <Medal className="w-6 h-6 text-orange-600" />
    return <span className="text-gray-600 font-bold">#{rank}</span>
  }

  const getGradeInfo = (percentage: number) => {
    if (percentage === 100) return { text: '💯 Hoàn hảo', color: 'text-purple-600 bg-purple-50' }
    if (percentage >= 90) return { text: '⭐ Xuất sắc', color: 'text-green-600 bg-green-50' }
    if (percentage >= 80) return { text: '👍 Tốt', color: 'text-blue-600 bg-blue-50' }
    if (percentage >= 70) return { text: '✓ Khá', color: 'text-orange-600 bg-orange-50' }
    return { text: '💪 Cố gắng', color: 'text-gray-600 bg-gray-50' }
  }

  const grade = getGradeInfo(result.percentage)

  return (
    <>
      <motion.tr
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: rank * 0.05 }}
        className={`cursor-pointer hover:bg-gray-50 transition-colors ${
          isCurrentUser ? 'bg-blue-50/50' : ''
        }`}
        onClick={onToggleDetails}
      >
        {/* Rank */}
        <td className="px-4 py-3">
          <div className="flex items-center gap-2">
            {getRankIcon(rank)}
            {rank <= 3 && (
              <motion.div
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                ✨
              </motion.div>
            )}
          </div>
        </td>

        {/* User */}
        <td className="px-4 py-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white font-bold text-sm">
              {result.userId.slice(0, 2).toUpperCase()}
            </div>
            <div>
              <div className="font-medium text-gray-900">
                {isCurrentUser ? 'Bạn' : `User ${result.userId.slice(-4)}`}
              </div>
              <div className="text-xs text-gray-500">Lần thử #{result.attempts}</div>
            </div>
          </div>
        </td>

        {/* Score */}
        <td className="px-4 py-3 text-center">
          <div className="inline-flex flex-col items-center">
            <div className="text-2xl font-bold text-gray-900">
              {result.percentage.toFixed(0)}%
            </div>
            <div className="text-xs text-gray-500">
              {result.score}/{result.maxScore}
            </div>
          </div>
        </td>

        {/* Time */}
        <td className="px-4 py-3 text-center">
          <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 rounded-full">
            <Clock className="w-4 h-4 text-blue-600" />
            <span className="font-mono font-bold text-blue-700">
              {formatTime(result.timeSpent)}
            </span>
          </div>
          {rank === 1 && sortBy === 'time' && (
            <div className="text-xs text-green-600 font-semibold mt-1">⚡ Nhanh nhất!</div>
          )}
        </td>

        {/* Rooms */}
        <td className="px-4 py-3 text-center">
          <div className="text-gray-700 font-medium">
            {result.roomsCompleted}/{result.maxScore}
          </div>
          <div className="flex justify-center gap-0.5 mt-1">
            {Array.from({ length: result.maxScore }).map((_, i) => (
              <div
                key={i}
                className={`w-2 h-2 rounded-sm ${
                  i < result.roomsCompleted ? 'bg-green-500' : 'bg-gray-300'
                }`}
              />
            ))}
          </div>
        </td>

        {/* Grade */}
        <td className="px-4 py-3 text-center">
          <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${grade.color}`}>
            {grade.text}
          </span>
        </td>

        {/* Date */}
        <td className="px-4 py-3 text-center text-sm text-gray-600">
          {formatDate(result.completedAt)}
        </td>
      </motion.tr>

      {/* Expandable Details */}
      <AnimatePresence>
        {showDetails && (
          <motion.tr
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <td colSpan={7} className="px-4 py-4 bg-gray-50">
              <GameResultDetails result={result} />
            </td>
          </motion.tr>
        )}
      </AnimatePresence>
    </>
  )
}

/**
 * Game Result Details Component
 */
const GameResultDetails: React.FC<{ result: GameResult }> = ({ result }) => {
  return (
    <div className="space-y-3">
      <h4 className="font-bold text-gray-800 flex items-center gap-2">
        <Target className="w-4 h-4" />
        Chi tiết câu trả lời:
      </h4>
      
      <div className="grid gap-2">
        {result.answers.map((answer, index) => (
          <div
            key={index}
            className={`p-3 rounded-lg border ${
              answer.isCorrect 
                ? 'bg-green-50 border-green-200' 
                : 'bg-red-50 border-red-200'
            }`}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-lg ${answer.isCorrect ? '✅' : '❌'}`}>
                    {answer.isCorrect ? '✅' : '❌'}
                  </span>
                  <span className="font-medium text-gray-700">
                    Phòng {answer.roomNumber}: {answer.questionText}
                  </span>
                </div>
                <div className="ml-7 space-y-1 text-sm">
                  <div className={answer.isCorrect ? 'text-green-700' : 'text-red-700'}>
                    <strong>Bạn chọn:</strong> {answer.userAnswer}
                  </div>
                  {!answer.isCorrect && (
                    <div className="text-green-700">
                      <strong>Đáp án đúng:</strong> {answer.correctAnswer}
                    </div>
                  )}
                </div>
              </div>
              <div className="text-xs text-gray-500 flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {answer.timeSpent}s
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-3 gap-3 mt-4 pt-4 border-t border-gray-200">
        <div className="text-center">
          <div className="text-sm text-gray-600">Tổng thời gian</div>
          <div className="text-lg font-bold text-gray-900">{formatTime(result.timeSpent)}</div>
        </div>
        <div className="text-center">
          <div className="text-sm text-gray-600">TB mỗi câu</div>
          <div className="text-lg font-bold text-gray-900">
            {(result.timeSpent / result.answers.length).toFixed(0)}s
          </div>
        </div>
        <div className="text-center">
          <div className="text-sm text-gray-600">Tỷ lệ đúng</div>
          <div className="text-lg font-bold text-gray-900">{result.percentage.toFixed(0)}%</div>
        </div>
      </div>
    </div>
  )
}

/**
 * Personal Best Component
 */
const PersonalBest: React.FC<{ gameId?: string; userId: string }> = ({ gameId, userId }) => {
  const { gameResults } = useStudyStore()
  
  const personalResults = useMemo(() => {
    let results = gameResults.filter(r => r.userId === userId)
    if (gameId) {
      results = results.filter(r => r.gameId === gameId)
    }
    return results.filter(r => r.won)
  }, [gameResults, userId, gameId])

  if (personalResults.length === 0) return null

  const bestScore = Math.max(...personalResults.map(r => r.percentage))
  const bestTime = Math.min(...personalResults.map(r => r.timeSpent))
  const totalGames = personalResults.length
  const avgScore = personalResults.reduce((sum, r) => sum + r.percentage, 0) / totalGames

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl shadow-lg p-6 text-white"
    >
      <div className="flex items-center gap-3 mb-4">
        <Star className="w-6 h-6" />
        <h3 className="text-xl font-bold">Thành tích cá nhân</h3>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <PersonalStat
          icon={<Trophy className="w-5 h-5" />}
          label="Điểm cao nhất"
          value={`${bestScore.toFixed(0)}%`}
        />
        <PersonalStat
          icon={<Zap className="w-5 h-5" />}
          label="Thời gian tốt nhất"
          value={formatTime(bestTime)}
        />
        <PersonalStat
          icon={<Target className="w-5 h-5" />}
          label="Điểm trung bình"
          value={`${avgScore.toFixed(0)}%`}
        />
        <PersonalStat
          icon={<Award className="w-5 h-5" />}
          label="Số lần thắng"
          value={`${totalGames}`}
        />
      </div>
    </motion.div>
  )
}

/**
 * Personal Stat Component
 */
const PersonalStat: React.FC<{
  icon: React.ReactNode
  label: string
  value: string
}> = ({ icon, label, value }) => (
  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 text-center">
    <div className="flex items-center justify-center gap-2 mb-2 text-white/90">
      {icon}
      <span className="text-xs font-medium">{label}</span>
    </div>
    <div className="text-2xl font-bold">{value}</div>
  </div>
)

/**
 * Format time from seconds to mm:ss
 */
const formatTime = (seconds: number): string => {
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

/**
 * Format date to readable string
 */
const formatDate = (date: Date): string => {
  const d = new Date(date)
  const now = new Date()
  const diff = now.getTime() - d.getTime()
  const hours = diff / (1000 * 60 * 60)
  const days = diff / (1000 * 60 * 60 * 24)
  
  if (hours < 1) return 'Vừa xong'
  if (hours < 24) return `${Math.floor(hours)}h`
  if (days < 7) return `${Math.floor(days)} ngày`
  
  return d.toLocaleDateString('vi-VN', { 
    day: '2-digit', 
    month: '2-digit',
    year: d.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
  })
}

/**
 * Mini Leaderboard - Compact version
 */
export const MiniLeaderboard: React.FC<{ gameId: string; limit?: number }> = ({ gameId, limit = 5 }) => {
  const { gameResults } = useStudyStore()
  
  const topResults = useMemo(() => {
    return gameResults
      .filter(r => r.gameId === gameId && r.won)
      .sort((a, b) => {
        if (a.percentage === b.percentage) {
          return a.timeSpent - b.timeSpent
        }
        return b.percentage - a.percentage
      })
      .slice(0, limit)
  }, [gameResults, gameId, limit])

  if (topResults.length === 0) return null

  return (
    <div className="bg-white rounded-lg shadow-md p-4 space-y-2">
      <h4 className="font-bold text-sm text-gray-700 flex items-center gap-2">
        <Trophy className="w-4 h-4 text-yellow-500" />
        Top {limit} Nhanh Nhất
      </h4>
      
      {topResults.map((result, index) => (
        <div
          key={result.id}
          className="flex items-center justify-between p-2 rounded-lg hover:bg-gray-50"
        >
          <div className="flex items-center gap-2">
            <span className={`text-sm font-bold ${
              index === 0 ? 'text-yellow-500' :
              index === 1 ? 'text-gray-400' :
              index === 2 ? 'text-orange-600' :
              'text-gray-600'
            }`}>
              #{index + 1}
            </span>
            <span className="text-sm text-gray-700">
              {result.percentage.toFixed(0)}%
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="w-3 h-3 text-gray-400" />
            <span className="text-sm font-mono text-gray-600">
              {formatTime(result.timeSpent)}
            </span>
          </div>
        </div>
      ))}
    </div>
  )
}

